import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { usePaymentActions } from "../../contexts/PaymentContext/PaymentContext";
import { PATH } from "../../router/path";

/**
 * Back from the payment page always means the cart. The entry behind this page can be
 * anything — the cart, a scanned entry link, LinkPass handing the customer back — and some
 * of those would carry the customer out of the app entirely. A guard entry is pushed over
 * the real history so back stays inside the app, and every pop lands in the cart.
 */
export const useBackReturnsToCart = (): void => {
	const navigate = useNavigate();
	const paymentActions = usePaymentActions();
	// The LinkPass return strips its auth params with a replace navigation, which writes
	// the router's own state over the guard entry. Keying on the search re-runs this
	// effect after that strip, and the marker check below re-arms the guard.
	const { search } = useLocation();

	useEffect(() => {
		if (!window.history.state?.paymentBackGuard) {
			// The router's own entry state rides along so its history tracking stays intact.
			window.history.pushState(
				{ ...window.history.state, paymentBackGuard: true },
				"",
				window.location.href
			);
		}

		const onPopState = () => {
			// Same as the header's back button: clear the order before the cart mounts, or
			// its checkout button sees an unpaid reference and bounces back to payment.
			paymentActions.CLEAR_PAYMENT_DATA();
			navigate(PATH.CART, { replace: true });
		};

		window.addEventListener("popstate", onPopState);

		return () => window.removeEventListener("popstate", onPopState);
	}, [navigate, paymentActions, search]);
};
